import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { Dual, IDualCalculatedTeamScores } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse, IKeyValue } from '../json-api/json-api.interface';

/**
 * Service to get and save dual data.
 */
@Injectable()
export class DualService {
    public static get INCLUDE_TOP_TEAM(): string {
        return 'topTeam';
    }

    public static get INCLUDE_BOTTOM_TEAM(): string {
        return 'bottomTeam';
    }

    public static get INCLUDE_ADJUSTMENTS(): string {
        return 'adjustments';
    }

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all duals for the specified event
     */
    public getIndex(eventId: string, includes: string[] = null, queryParams: string[] = null): Observable<Dual[]> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const included: any = response.included;
                const duals: Dual[] = [];
                const bodyData: any = response.data || [];
                for (const dual of bodyData) {
                    duals.push((new Dual()).deserialize(dual, included));
                }
                return duals;
            }));
    }

    /**
     * Get a single schedule item's data
     */
    public get(
        eventId: string,
        dualId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<Dual> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, dualId, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new Dual().deserialize(response))));
    }

    /**
     * Add or update the dual
     */
    public save(dual: Dual, includes: string[] = null, queryParams: string[] = null): Observable<Dual> {
        queryParams = this.appendGetFields(queryParams);
        let response: Observable<IJsonApiResponse>;
        const dualId: string = !dual.isNew ? dual.id : null;
        const apiRequestUrl: string = this.getApiUrl(dual.eventId, dualId, includes, queryParams);

        if (!dual.isNew) {
            // Update existing Dual
            response = this.http.patch(apiRequestUrl, dual.serialize());
        } else {
            // Add a new Dual
            response = this.http.post(apiRequestUrl, dual.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new Dual()).deserialize(response)));
    }

    /**
     * Get the calculated team scores for the specified dual.
     */
    public getCalculatedTeamScores(eventId: string, dualId: string): Observable<IDualCalculatedTeamScores> {
        const apiRequestUrl: string = this.getApiUrl(eventId, `${dualId}/calculated-team-scores`);
        return this.http.get<IDualCalculatedTeamScores>(apiRequestUrl, false)
            .pipe(
                map((response: IDualCalculatedTeamScores) => response as IDualCalculatedTeamScores),
            );
    }

    /**
     * Update the team scores for the specified dual.
     */
    public updateTeamScores(eventId: string, dualId: string, includes: string[] = null): Observable<Dual> {
        const apiRequestUrl: string = this.getApiUrl(eventId, `${dualId}/update-team-scores`, includes);
        return this.http.patch(apiRequestUrl, null, false)
            .pipe(map((response: IJsonApiResponse) => (new Dual()).deserialize(response)));
    }

    /**
     * Delete the specified dual.
     */
    public delete(eventId: string, dualId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, dualId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        dualId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/duals/`;

        if (dualId) {
            url += dualId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[dual]') !== -1).length === 0) {
            queryParams.push(`fields[dual]=${Dual.getFields().join(',')}`);
        }

        return queryParams;
    }
}
